import {PasswordResponse, Provider, PROVIDER_STATE_NAMES, Providers, ProviderState} from '@/api/models/Providers.ts';
import ConfirmDeleteDialog, {ConfirmFn} from '@/components/ConfirmDeleteDialog.tsx';
import NoSearchResults from '@/components/NoSearchResults.tsx';
import Pending from '@/components/Pending.tsx';
import Search from '@/components/Search.tsx';
import TitleWithDropdown from '@/components/TitleWithDropdown.tsx';
import ConfirmPasswordResetDialog from '@/pages/providers/ConfirmPasswordResetDialog.tsx';
import CreateProviderDialog, {SavedHandlerFn} from '@/pages/providers/CreateProviderDialog.tsx';
import PasswordCopyDialog from '@/pages/providers/PasswordCopyDialog.tsx';
import {Toaster, ToastErrors} from '@/toaster.ts';
import {Button, HTMLTable, Icon, MenuItem, NonIdealState, Spinner, SpinnerSize, Tooltip} from '@blueprintjs/core';
import {ReactElement, useCallback, useMemo, useState} from 'react';

export default function ProvidersPage(): ReactElement {
	const [sequence, setSequence] = useState(0);
	const providers = useMemo(Providers.list, [sequence]);

	const [copyPassword, setCopyPassword] = useState<string | null>(null);

	const onCopyPasswordDialogClose = useCallback(() => {
		setCopyPassword(null);
		setSequence(seq => seq + 1);
	}, []);

	const [showCreateDialog, setShowCreateDialog] = useState(false);

	const onCreateDialogClose = useCallback(() => {
		setShowCreateDialog(false);
	}, []);

	const onCreateDialogSaved: SavedHandlerFn = useCallback(response => {
		setShowCreateDialog(false);
		setCopyPassword(response.password);
	}, []);

	const onAddProviderClick = useCallback(() => {
		setShowCreateDialog(true);
	}, []);

	const onRefreshClick = useCallback(() => {
		setSequence(seq => seq + 1);
	}, []);

	const [filter, setFilter] = useState('');

	return (
		<>
			<TitleWithDropdown title="Providers" rightElement={<Search onSearch={setFilter} />}>
				<MenuItem icon="refresh" text="Refresh" onClick={onRefreshClick} />
				<MenuItem icon="add" text="Add Provider" onClick={onAddProviderClick} />
			</TitleWithDropdown>

			<Pending resource={providers}>
				{providers => (
					<List
						providers={providers}
						filter={filter}
						onAddProviderClick={onAddProviderClick}
						setCopyPassword={setCopyPassword}
						refresh={onRefreshClick}
					/>
				)}
			</Pending>

			{showCreateDialog && (
				<CreateProviderDialog onClose={onCreateDialogClose} onSaved={onCreateDialogSaved} />
			)}

			{copyPassword && (
				<PasswordCopyDialog password={copyPassword} onClose={onCopyPasswordDialogClose} />
			)}
		</>
	);
}

interface Props {
	providers: Provider[],
	filter: string,
	onAddProviderClick: () => void,
	setCopyPassword: (password: string) => void,
	refresh: () => void,
}

function List({providers, onAddProviderClick, setCopyPassword, refresh, filter}: Props): ReactElement {
	const filtered = useMemo(() => {
		if (filter.length === 0)
			return providers;

		return providers.filter(v => v.username.toLocaleLowerCase().includes(filter));
	}, [providers, filter]);

	const [deleteTarget, setDeleteTarget] = useState<Provider | null>(null);

	const onDeleteConfirm: ConfirmFn = useCallback(async () => {
		if (!deleteTarget)
			throw new Error('onDeleteConfirm() called with no deleteTarget set');

		try {
			await Providers.remove(deleteTarget.id);
		} catch (error) {
			ToastErrors.fromError(error);
			return;
		}

		Toaster.success('The provider is being deleted.');
		setDeleteTarget(null);

		refresh();
	}, [deleteTarget, refresh]);

	const onDeleteCancel = useCallback(() => {
		setDeleteTarget(null);
	}, []);

	const [resetTarget, setResetTarget] = useState<Provider | null>(null);

	const onResetConfirm: ConfirmFn = useCallback(async () => {
		if (!resetTarget)
			throw new Error('onResetConfirm() called with no resetTarget set');

		let response: PasswordResponse;

		try {
			response = await Providers.resetPassword(resetTarget.id);
		} catch (error) {
			ToastErrors.fromError(error);
			return;
		}

		setResetTarget(null);
		setCopyPassword(response.password);

		Toaster.success('The provider\'s password has been reset.');
	}, [resetTarget, setCopyPassword]);

	const onResetCancel = useCallback(() => {
		setResetTarget(null);
	}, []);

	if (providers.length === 0) {
		return (
			<NonIdealState
				icon="wind"
				title="Provider? But I hardly know 'er..."
				description={(
					<>
						You currently have no providers<br />
						Click the button below to add one!
					</>
				)}
				action={(
					<Button
						icon="add"
						intent="primary"
						variant="outlined"
						text="Add Provider"
						onClick={onAddProviderClick}
					/>
				)}
			/>
		);
	} else if (filtered.length === 0)
		return <NoSearchResults />;

	return (
		<>
			<HTMLTable striped={true} interactive={true} style={{width: '100%'}}>
				<thead>
					<tr>
						<th>ID.</th>
						<th>Username</th>
						<th>Status</th>
						<th>&nbsp;</th>
					</tr>
				</thead>

				<tbody>
					{filtered.map(v => (
						<Row
							key={v.id}
							provider={v}
							onDelete={setDeleteTarget}
							onPasswordReset={setResetTarget}
						/>
					))}
				</tbody>
			</HTMLTable>

			{deleteTarget && (
				<ConfirmDeleteDialog
					confirmText={deleteTarget.username}
					onConfirm={onDeleteConfirm}
					onClose={onDeleteCancel}
					isOpen={true}
				/>
			)}

			{resetTarget && (
				<ConfirmPasswordResetDialog
					provider={resetTarget}
					onConfirm={onResetConfirm}
					onClose={onResetCancel}
					isOpen={true}
				/>
			)}
		</>
	);
}

type ProviderDeleteFn = (provider: Provider) => void;
type ProviderResetPasswordFn = (provider: Provider) => void;

interface RowProps {
	provider: Provider,
	onDelete: ProviderDeleteFn,
	onPasswordReset: ProviderResetPasswordFn,
}

function Row({provider, onDelete, onPasswordReset}: RowProps): ReactElement {
	const onDeleteClick = useCallback(() => {
		onDelete(provider);
	}, [onDelete, provider]);

	const onPasswordResetClick = useCallback(() => {
		onPasswordReset(provider);
	}, [onPasswordReset, provider]);

	const stateIndicator = useMemo(() => {
		switch (provider.state) {
			case ProviderState.Ok:
				return null;
			case ProviderState.Failed:
				return (
					<Tooltip content="A recent change to this profile failed. Please contact a developer!">
						<Icon icon="warning-sign" intent="warning" />
					</Tooltip>
				);
			default:
				return (
					<Spinner
						intent="primary"
						size={SpinnerSize.SMALL}
						style={{display: 'inline-block', marginRight: 5}}
					/>
				);
		}
	}, [provider.state]);

	return (
		<tr>
			<td>{provider.id}</td>
			<td>{provider.username}</td>
			<td>{PROVIDER_STATE_NAMES[provider.state]} {stateIndicator}</td>
			<td style={{display: 'flex', justifyContent: 'end', gap: 10}}>
				<Tooltip content="Reset Password">
					<Button size="small" variant="minimal" icon="key" intent="warning" onClick={onPasswordResetClick} />
				</Tooltip>

				<Tooltip content="Delete">
					<Button size="small" variant="minimal" icon="delete" intent="warning" onClick={onDeleteClick} />
				</Tooltip>
			</td>
		</tr>
	);
}
